<template>
	<div class="body_profitability_analysis">
		<div v-if="isLoading" class="state_message"><p>Cargando análisis…</p></div>
		<div v-else-if="error" class="state_message"><p class="error_text">{{ error }}</p></div>
		<template v-else-if="data">
			<div class="cards_row">
				<CategoryMetricCard v-for="card in cards" :key="card.key"
					:label="card.label"
					:amount="card.amount"
					:pills="card.pills"
					:items="card.shareItems" />
			</div>
			<p v-if="!cards.length" class="state_message">
				Sin datos de {{ categoryLabel }} para este mes.
			</p>

			<ItemDetailSection
				:data="detailData"
				:is-loading="isDetailLoading"
				:error="detailError || ''"
				:query="counterpartsQuery"
				:counterparts="filteredCounterparts"
				:selected-account="detailAccount"
				:selected-level="detailAccountLevel"
				:items="itemsBreakdown"
				:category-plural="categoryPlural"
				:category-singular="categoryLabel"
				:selected-item="detailItem"
				:items-total="itemsTotal"
				:mode="detailMode"
				@expand="onDetailExpand"
				@update:query="setCounterpartsQuery"
				@select-account="selectDetailRankingAccount"
				@select-item="selectDetailItem"
				@update:mode="setDetailMode"
				@counterpart-click="openCounterpartDetail" />

			<ProfitabilityCategoryEvolution class="evolution"
				:months="evolutionMonths"
				:metrics="evolutionMetrics"
				:items="items" />
		</template>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import CategoryMetricCard from "./CategoryMetricCard.vue";
import ItemDetailSection from "./ItemDetailSection.vue";
import ProfitabilityCategoryEvolution from "./ProfitabilityCategoryEvolution.vue";
import { clientDetailModalConfig }
	from "@/components/financialReports/salesAnalysis/salesDrawerModals";
import { obligationForType } from "@/utils/counterpartObligation";
import { formatVariation } from "@/utils/percentage";

// Cuerpo del drawer "Análisis de Categorías" (Rentabilidad): lee el módulo
// `profitabilityAnalysis` y reparte slices a cards presentacionales, como
// BodySalesAnalysis. Sin props: el modal pasa `bodyProps: null`.
//
// La card de Ventas lleva los badges MoM/YoY; la de cada margen, su % sobre
// ventas. Las barras reparten ese monto entre los 3 ítems de mayor magnitud
// más "Otros" con el resto acumulado (el recorte lo hace el backend: con una
// docena de tiendas, una barra por cada una era ilegible). El backend manda
// `share` como fracción signada; acá va a 0-100.
const SALES_KEY = "ventas";
// Un color por ítem, estable entre cards: el orden de `items` lo fija el
// backend (por venta del mes) y es el mismo que usa la evolución.
const ITEM_COLORS = [
	"var(--progress-bar-1)",
	"var(--progress-bar-2)",
	"var(--progress-bar-3)",
	"var(--progress-bar-4)",
	"var(--progress-bar-5)",
];
// El resto acumulado no es un ítem: neutro propio, fuera de la rueda de
// colores (el backend lo marca con `is_rest`, no por su label).
const REST_COLOR = "var(--progress-bar-otros)";

export default {
	name: "BodyProfitabilityAnalysis",
	components: { CategoryMetricCard, ItemDetailSection, ProfitabilityCategoryEvolution },

	computed: {
		...mapState("profitabilityAnalysis", [
			"data", "isLoading", "error",
			"month", "category", "detailData", "isDetailLoading",
			"detailError", "counterpartsQuery",
			"detailAccount", "detailAccountLevel", "detailItem", "detailMode",
		]),
		...mapGetters("profitabilityAnalysis", ["filteredCounterparts"]),

		items() {
			return (this.data && this.data.items) || [];
		},

		evolutionMonths() {
			return (this.data && this.data.evolution && this.data.evolution.months) || [];
		},

		evolutionMetrics() {
			return (this.data && this.data.evolution && this.data.evolution.metrics) || [];
		},

		categoryLabel() {
			return (this.data && this.data.category_label) || "la categoría";
		},

		// Plural tal cual lo escribió el analista al configurar la categoría
		// ("tiendas", "marcas"): es el nombre con el que la reconoce.
		categoryPlural() {
			return (this.data && this.data.category) || "ítems";
		},

		// Costos del mes por ítem, con el color que ese ítem tiene en las
		// cards y en la evolución.
		itemsBreakdown() {
			return ((this.data && this.data.items_breakdown) || []).map((item) => ({
				...item,
				color: this.colorByItem[item.name],
			}));
		},

		// Total de la fila "todas": la venta del mes de toda la categoría, que
		// no cambia cuando el analista elige un ítem.
		itemsTotal() {
			return this.itemsBreakdown.reduce((total, item) => total + item.sales, 0);
		},

		// Color por ítem, por nombre: la misma tienda conserva su color en las
		// cuatro cards aunque su posición cambie de un escalón a otro.
		colorByItem() {
			return this.items.reduce((acc, name, i) => {
				acc[name] = ITEM_COLORS[i % ITEM_COLORS.length];
				return acc;
			}, {});
		},

		cards() {
			const cards = (this.data && this.data.cards) || [];
			const headline = (this.data && this.data.headline) || {};
			return cards.map((card) => ({
				key: card.key,
				label: card.label,
				amount: card.amount,
				pills: card.key === SALES_KEY
					? [
						this.variationPill(headline.mom, "MoM"),
						this.variationPill(headline.yoy, "YoY"),
					]
					// El % de un escalón se lee sobre las ventas del mes: en una
					// cascada de márgenes no hay otro denominador posible.
					: [{ text: `${Math.round(card.percent)}%`, tone: "accent" }],
				shareItems: (card.items || []).map((item) => ({
					label: item.name,
					percent: Math.round(item.share * 100),
					color: item.is_rest ? REST_COLOR : this.colorByItem[item.name],
					isRest: Boolean(item.is_rest),
				})),
			}));
		},
	},

	methods: {
		// Badge de un delta: el semáforo de variaciones del reporte en vez del
		// cyan neutro — una caída se tiene que leer como caída. Sin comparable
		// el badge queda neutro con su guion.
		variationPill(ratio, suffix) {
			let tone = "accent";
			if (ratio > 0) tone = "positive";
			else if (ratio < 0) tone = "negative";
			return { text: `${formatVariation(ratio)} ${suffix}`, tone };
		},

		...mapActions("profitabilityAnalysis", [
			"fetchItemDetail", "selectDetailRankingAccount", "selectDetailItem",
			"setDetailMode",
		]),
		...mapMutations("profitabilityAnalysis", {
			setCounterpartsQuery: "SET_COUNTERPARTS_QUERY",
		}),
		...mapActions("clientDetail", {
			openClientDetailAction: "openClientDetail",
			closeClientDetailAction: "closeClientDetail",
			changeClientRangePeriod: "changeRangePeriod",
		}),

		// El detalle se pide al desplegar la sección, no al abrir el drawer.
		onDetailExpand() {
			if (this.detailData) return;
			this.fetchItemDetail();
		},

		// Drill a la contraparte: mismo drawer apilado que Costos (el detalle
		// del ítem sale de /costs/analysis). Lado por pagar y subject por
		// level_1 "Costos", igual que allá, para que el modal sea coherente.
		openCounterpartDetail(counterpart) {
			if (!counterpart || !counterpart.name || !this.month) return;
			this.openClientDetailAction({
				alias: counterpart.name,
				documentType: "P",
				monthIso: this.month,
				obligationName: obligationForType(counterpart.counterpart_type),
				level1Name: "Costos",
				counterpartType: counterpart.counterpart_type,
			});
			// Segundo nivel apilado sobre el drawer de categorías → 70 % deja
			// ver el modal padre; el theme es el del tab.
			this.$buefy.modal.open({
				...clientDetailModalConfig({
					fallbackAlias: counterpart.name,
					width: "70%",
					theme: "rentabilidad",
				}),
				parent: this,
				events: {
					closed: this.closeClientDetailAction,
					"update-range-period": this.changeClientRangePeriod,
				},
			});
		},
	},
};
</script>

<style scoped lang="scss">
.body_profitability_analysis {
	padding: 0 20px 24px;
}

// Los escalones son una cascada que se lee en fila: una columna por card,
// todas del mismo ancho (nada de `auto-fit`, que con 4 cards y un mínimo alto
// desbordaba el drawer y recortaba la primera).
.cards_row {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	align-items: stretch;
}

@media (max-width: 1180px) {
	.cards_row {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 680px) {
	.cards_row {
		grid-template-columns: minmax(0, 1fr);
	}
}

.evolution {
	margin-top: 16px;
}

.state_message {
	padding: 32px 0;
	text-align: center;
	color: var(--report-text-muted);
	font-size: 14px;
}

.error_text {
	color: var(--report-negative);
}
</style>
